import { memo, useMemo, useState } from 'react';
import { ContentItemBoxListContainer } from 'ContentItemBox';
import { CourseItemBox } from 'CourseItemBox';
import { type Stream } from 'Lessons';
import { type ProgressStatus } from 'ContentProgress';
import { regionAwareImageUrlForFormat } from 'regionAwareImage';
import { BlockLink } from 'BlockLink';
import { useTranslation } from 'react-i18next';
import './locales/bookmarkedCourses/bookmarkedCourses-en.json';

const initialCourseLimit = 4;

export const CourseList = memo(
    ({
        courses,
        header,
        onPress,
        getProgressInfo,
        initiallyLimitShownCourses = false,
    }: {
        courses: Stream[];
        header: string;
        onPress: (course: Stream) => void;
        getProgressInfo: (stream: Stream) => { status: ProgressStatus } | null;
        initiallyLimitShownCourses?: boolean;
    }) => {
        const { t } = useTranslation('back_royal', { keyPrefix: 'bookmarkedCourses.bookmarkedCourses' });
        const [limitShownCourses, setLimitShownCourses] = useState(
            initiallyLimitShownCourses && courses.length > initialCourseLimit,
        );
        const shownCourses = useMemo(
            () => (limitShownCourses ? courses.slice(0, initialCourseLimit) : courses),
            [limitShownCourses, courses],
        );
        if (shownCourses.length === 0) return null;

        //  This has to be wrapped in a div so the flex conatiner above it doesn't
        // add spacing between the list and the Read More link
        return (
            <div>
                <ContentItemBoxListContainer header={header}>
                    {shownCourses.map(course => (
                        <CourseItemBox
                            key={course.id}
                            showSubtitle={false}
                            localePack={course.localePack}
                            src={regionAwareImageUrlForFormat(course.image, 'original')!}
                            progressInfo={getProgressInfo(course)}
                            onPress={() => onPress(course)}
                        />
                    ))}
                </ContentItemBoxListContainer>

                {limitShownCourses && (
                    <div className="mt-3.5 flex justify-center">
                        <BlockLink
                            onPress={() => setLimitShownCourses(false)}
                            anchorIcon="chevron-down"
                            color="slate-grey"
                        >
                            {t('showMore')}
                        </BlockLink>
                    </div>
                )}
            </div>
        );
    },
);
